import React from 'react';
import { motion } from 'framer-motion';
import { useCurrency } from '../hooks/useCurrency';
import { useOrderBumps } from '../hooks/useOrderBumps';
import { CTAButton } from './CTAButton';
// Realistic local prices for therapist & coach per currency (NOT converted from USD)
const LOCAL_PRICES: Record<string, {
  therapist: string;
  coach: string;
  total: string;
}> = {
  USD: {
    therapist: '$150',
    coach: '$200',
    total: '$350'
  },
  CAD: {
    therapist: 'CA$150',
    coach: 'CA$200',
    total: 'CA$350'
  },
  GBP: {
    therapist: '£100',
    coach: '£130',
    total: '£230'
  },
  AUD: {
    therapist: 'A$180',
    coach: 'A$220',
    total: 'A$400'
  },
  EUR: {
    therapist: '€120',
    coach: '€160',
    total: '€280'
  },
  MXN: {
    therapist: 'MX$1,500',
    coach: 'MX$2,000',
    total: 'MX$3,500'
  },
  COP: {
    therapist: 'COL$250,000',
    coach: 'COL$350,000',
    total: 'COL$600,000'
  },
  ARS: {
    therapist: 'AR$30,000',
    coach: 'AR$45,000',
    total: 'AR$75,000'
  },
  BRL: {
    therapist: 'R$400',
    coach: 'R$550',
    total: 'R$950'
  },
  CLP: {
    therapist: 'CL$80,000',
    coach: 'CL$120,000',
    total: 'CL$200,000'
  },
  PEN: {
    therapist: 'S/250',
    coach: 'S/350',
    total: 'S/600'
  },
  NZD: {
    therapist: 'NZ$170',
    coach: 'NZ$220',
    total: 'NZ$390'
  }
};
export function PriceComparison() {
  const {
    formatPrice,
    currencyCode
  } = useCurrency();
  const {
    goToCheckout
  } = useOrderBumps();
  const prices = LOCAL_PRICES[currencyCode] || LOCAL_PRICES['USD'];
  return <section className="py-16 md:py-20 px-6 bg-cream" data-id="element-264">
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.6
    }} className="max-w-lg mx-auto text-center" data-id="element-265">
        {/* Price anchoring — compact */}
        <p className="text-xs uppercase tracking-[0.2em] text-brown/40 font-bold mb-8" data-id="element-266">
          Put it in perspective
        </p>

        <div className="space-y-3 mb-10" data-id="element-267">
          <div className="flex items-center justify-between py-3 border-b border-brown/8" data-id="element-268">
            <span className="text-brown/70 text-sm" data-id="element-269">Postpartum therapist</span>
            <span className="text-brown/40 text-sm line-through" data-id="element-270">
              {prices.therapist}+ / session
            </span>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-brown/8" data-id="element-271">
            <span className="text-brown/70 text-sm" data-id="element-272">Nervous system coach</span>
            <span className="text-brown/40 text-sm line-through" data-id="element-273">
              {prices.coach}+ / hr
            </span>
          </div>
          {/* Combined total struck through */}
          <div className="pt-4 pb-2 text-center" data-id="element-274">
            <p className="text-brown/40 text-xs uppercase tracking-widest font-bold mb-1" data-id="element-275">
              Total value
            </p>
            <span className="text-brown/30 text-2xl md:text-3xl line-through font-serif" data-id="element-276">
              {prices.total}+
            </span>
          </div>

          <div className="py-6 text-center" data-id="element-277">
            <p className="font-serif text-lg text-brown/80 mb-1" data-id="element-278">
              The Nap-Trapped Reset
            </p>
            <div className="flex items-baseline justify-center gap-3" data-id="element-279">
              <span className="text-brown/25 text-lg line-through" data-id="element-280">
                {formatPrice(35)}
              </span>
              <span className="font-serif text-5xl md:text-6xl font-bold text-terracotta tracking-tight" data-id="element-281">
                {formatPrice(14)}
              </span>
            </div>
            <p className="text-brown/40 text-xs mt-2 tracking-wide" data-id="element-282">
              One-time payment · Yours forever
            </p>
          </div>
        </div>

        <p className="font-serif text-lg md:text-xl text-brown/70 italic mb-10 leading-relaxed" data-id="element-283">
          Less than a coffee. Yours forever.
        </p>

        <CTAButton text="Give Me My 30 Minutes" onClick={goToCheckout} data-id="element-284" />

        <p className="text-xs text-brown/40 mt-5 tracking-wide" data-id="element-285">
          Instant PDF · Phone-friendly · 14-day guarantee
        </p>
      </motion.div>
    </section>;
}